import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import {
  createNewJobAd,
  EditJobAdPayload,
  FetchGetJobAdIdByBundleId,
  getJobAdById,
  getJobAdIdByBundleId,
  getJobAds,
  JobAdFetch,
  JobAdsFilters,
  postCloseJobAd,
  postReOpenJobAd,
  postCreateJobAdIntegration,
  postEditJobAd,
  postEditJobAdIntegration,
  postPublishJobAd,
  VersionedJobAdPayload,
} from "./jobAdAPI";
import {
  createCommandAsyncThunk,
  createMsgCommandAsyncThunk,
} from "../../app/createApiAsyncThunk";
import { RootState } from "../../app/store";
import { EventPayload } from "../../common/types/Event";
import {
  JobAdCreated,
  JobAdEdited,
  JobAdEventName,
  JobAdIntegrationPayload,
  JobAdPublished,
  JobAdReOpened,
} from "./types/events";
import {
  JobAd,
  JobAdIntegration,
  JobAdIntegrationDetails,
  JobAdIntegrationType,
  JobAdStatus,
} from "./types/common";
import { Hits, Paginated } from "../../common/types/Paginated";

// TODO: Improve typings

export type SliceState = {
  ids: string[];
  entities: {
    [id: string]: JobAd;
  };
  cursor: string | null;
  hits?: Hits;
  status: "idle" | "pending" | "succeeded" | "failed";
};

const jobAdsAdapter = createEntityAdapter<JobAd, JobAd["id"]>({
  selectId: (jobAd) => jobAd.id || "",
});

export const fetchJobAds = createAsyncThunk<
  Paginated<JobAd[]>,
  JobAdsFilters,
  { state: RootState }
>("jobAds/fetchJobAds", async (filters: JobAdsFilters, { requestId }) => {
  const result = await getJobAds(filters, { requestId });
  return result.data;
});

export const fetchJobAdById = createAsyncThunk<
  JobAdFetch,
  string,
  { state: RootState }
>("jobAds/fetchJobAdById", async (id: string, { requestId }) => {
  return await getJobAdById(id, { requestId });
});

export const createJobAd = createAsyncThunk<
  EventPayload<JobAdCreated>,
  JobAd,
  { state: RootState }
>(
  "jobAds/createJobAd",
  async (jobAd: JobAd, { requestId }) =>
    await createNewJobAd(jobAd, { requestId }),
);

export const editJobAd = createMsgCommandAsyncThunk<
  EditJobAdPayload,
  EventPayload<JobAdEdited>
>("jobAds/editJobAd", postEditJobAd);

export const createJobAdIntegration = createAsyncThunk<
  EventPayload<JobAdIntegrationPayload>,
  JobAdIntegration,
  { state: RootState }
>(
  "jobAds/createJobAdIntegration",
  async (jobAd: JobAdIntegration, { requestId }) =>
    await postCreateJobAdIntegration(jobAd, { requestId }),
);

export const editJobAdIntegration = createAsyncThunk<
  EventPayload<JobAdIntegrationPayload>,
  JobAdIntegration,
  { state: RootState }
>(
  "jobAds/editJobAdIntegration",
  async (jobAd: JobAdIntegration, { requestId }) =>
    await postEditJobAdIntegration(jobAd, { requestId }),
);

export const fetchGetJobAdIdByBundleId = createAsyncThunk<
  FetchGetJobAdIdByBundleId,
  string,
  { state: RootState }
>(
  "jobAds/fetchGetJobAdIdByBundleId",
  async (bundleId: string, { requestId }) => {
    return await getJobAdIdByBundleId(bundleId, { requestId });
  },
);

export const publishJobAd = createCommandAsyncThunk<
  VersionedJobAdPayload,
  JobAdPublished | JobAdIntegrationPayload
>("jobAds/publishJobAd", postPublishJobAd);

export const closeJobAd = createMsgCommandAsyncThunk<
  VersionedJobAdPayload,
  any
>("jobAds/closeJobAd", postCloseJobAd);

export const reOpenJobAd = createCommandAsyncThunk<
  VersionedJobAdPayload,
  JobAdReOpened
>("jobAds/reOpenJobAd", postReOpenJobAd);

const updateIntegration = (
  updatedIntegration: JobAdIntegrationDetails,
  integrationType: JobAdIntegrationType,
  state: SliceState,
  version: number,
) => {
  const jobAdToUpdate = state.entities[updatedIntegration.jobAdId];
  const integrations = jobAdToUpdate.integrations || [];

  const integrationIndex = integrations.findIndex(
    (i) => i.integration.type === integrationType,
  );

  integrations[integrationIndex].integration = updatedIntegration;
  integrations[integrationIndex].version = version;

  const changes = {
    integrations,
  };

  jobAdsAdapter.updateOne(state, {
    id: updatedIntegration.jobAdId,
    changes: changes,
  });
};

const jobAdsSlice = createSlice({
  name: "jobAds",
  initialState: jobAdsAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobAds.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchJobAds.fulfilled, (state, action) => {
        const jobAds = action.payload.data
          .sort((a, b) =>
            a.creationDate && b.creationDate
              ? new Date(b.creationDate).getTime() -
                new Date(a.creationDate).getTime()
              : 0,
          )
          .map((jobAd) => {
            return {
              ...jobAd,
              id: jobAd.id,
            };
          });

        if (!action.meta.arg.cursor) {
          jobAdsAdapter.setAll(state, jobAds);
        } else {
          jobAdsAdapter.upsertMany(state, jobAds);
        }

        state.cursor = action.payload.cursor;
        state.hits = action.payload.hits;
        state.status = "succeeded";
      })
      .addCase(fetchJobAds.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchJobAdById.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchJobAdById.fulfilled, (state, action) => {
        const jobAd = action.payload;
        if (!jobAd.id) {
          return;
        }

        if (state.entities[jobAd.id]) {
          jobAdsAdapter.updateOne(state, {
            id: jobAd.id,
            changes: { ...jobAd },
          });
        } else {
          jobAdsAdapter.addOne(state, jobAd);
        }

        state.status = "succeeded";
      })
      .addCase(fetchJobAdById.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(createJobAd.pending, (state) => {
        state.status = "pending";
      })
      .addCase(createJobAd.fulfilled, (state, action) => {
        const newJobAd: JobAd = {
          ...action.payload.payload.jobAd,
          id: action.payload.id,
          version: action.payload.version,
        };

        jobAdsAdapter.addOne(state, newJobAd);
        state.status = "succeeded";
      })
      .addCase(createJobAd.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editJobAd.pending, (state) => {
        state.status = "pending";
      })
      .addCase(editJobAd.fulfilled, (state, action) => {
        const { id, version, payload } = action.payload;

        const changes = {
          ...payload.jobAd,
          version,
        };
        jobAdsAdapter.updateOne(state, {
          id: id,
          changes: changes,
        });
        state.status = "succeeded";
      })
      .addCase(editJobAd.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(createJobAdIntegration.pending, (state) => {
        state.status = "pending";
      })
      .addCase(createJobAdIntegration.fulfilled, (state, action) => {
        const { id, version, payload } = action.payload;

        const jobAdToUpdate =
          state.entities[payload.integrationDetails.jobAdId];
        const integrations = jobAdToUpdate.integrations || [];

        integrations.push({
          id: id,
          version,
          integration: payload.integrationDetails,
        });

        const changes = {
          integrations,
        };

        jobAdsAdapter.updateOne(state, {
          id: payload.integrationDetails.jobAdId,
          changes: changes,
        });

        state.status = "succeeded";
      })
      .addCase(createJobAdIntegration.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editJobAdIntegration.pending, (state) => {
        state.status = "pending";
      })
      .addCase(editJobAdIntegration.fulfilled, (state, action) => {
        const { version, payload } = action.payload;

        if (payload.integrationDetails.type) {
          updateIntegration(
            payload.integrationDetails,
            payload.integrationDetails.type,
            state,
            version,
          );
        }

        state.status = "succeeded";
      })
      .addCase(editJobAdIntegration.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(publishJobAd.pending, (state) => {
        state.status = "pending";
      })
      .addCase(publishJobAd.fulfilled, (state, action) => {
        for (const payload of action.payload) {
          const { id, version } = payload;
          if (payload.name === JobAdEventName.JobAdPublished) {
            const changes = {
              version,
              status: JobAdStatus.PUBLISHED,
            };

            jobAdsAdapter.updateOne(state, {
              id: id,
              changes: changes,
            });
          } else {
            if (
              "integrationDetails" in payload.payload &&
              payload.payload.integrationDetails.type
            ) {
              updateIntegration(
                payload.payload.integrationDetails,
                payload.payload.integrationDetails.type,
                state,
                version,
              );
            }
          }
        }

        state.status = "succeeded";
      })
      .addCase(publishJobAd.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(closeJobAd.pending, (state) => {
        state.status = "pending";
      })
      .addCase(closeJobAd.fulfilled, (state, action) => {
        for (const payload of action.payload) {
          const { id, version } = payload;
          if (payload.name === JobAdEventName.JobAdClosed) {
            const changes = {
              version,
              status: JobAdStatus.CLOSED,
            };

            jobAdsAdapter.updateOne(state, {
              id: id,
              changes: changes,
            });
          } else {
            if (
              "integrationDetails" in payload.payload &&
              payload.payload.integrationDetails.type
            ) {
              updateIntegration(
                payload.payload.integrationDetails,
                payload.payload.integrationDetails.type,
                state,
                version,
              );
            }
          }
        }

        state.status = "succeeded";
      })
      .addCase(closeJobAd.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(reOpenJobAd.pending, (state) => {
        state.status = "pending";
      })
      .addCase(reOpenJobAd.fulfilled, (state, action) => {
        for (const payload of action.payload) {
          const { id, version } = payload;
          if (payload.name === JobAdEventName.JobAdReOpened) {
            const changes = {
              version,
              status: JobAdStatus.DRAFT,
            };

            jobAdsAdapter.updateOne(state, {
              id: id,
              changes: changes,
            });
          }
        }

        state.status = "succeeded";
      })
      .addCase(reOpenJobAd.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const { selectAll: selectAllJobAds, selectById: selectJobAdById } =
  jobAdsAdapter.getSelectors((state: RootState) => state.jobAds);

export default jobAdsSlice.reducer;
